import { isSupported } from 'firebase/messaging';

import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import useAlert from 'hooks/useAlert';
import useRefreshToken from 'hooks/useRefreshToken';

import axios from 'services/axios';
import { REGISTER } from 'services/consts';
import { deleteFCMToken } from 'services/firebase';

import { setLoading } from 'stores/layoutSlice';

import AppLogger from 'utils/AppLogger';
import FirebaseAuthService from 'utils/FirebaseAuthService';
import PATHS from 'utils/paths';

const useFedCM = (autoLogin = false) => {
	const refreshAccess = useRefreshToken();
	const { state } = useLocation();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { showError } = useAlert();
	const dispatch = useDispatch();

	const handleGoogleLogin = async ({ credential }) => {
		dispatch(setLoading(true));

		try {
			const userRef = await FirebaseAuthService.loginWithGoogleCredential(credential);

			const response = await axios.post(REGISTER.CHECK_REGISTER, {
				UID: userRef.user.uid,
				email: userRef.user.email,
				refreshToken: userRef.user.stsTokenManager.refreshToken
			});

			const isRegistered = response.data.registered;

			// remove FCM token
			try {
				await isSupported().then(async (isSupported) => {
					isSupported && (await deleteFCMToken());
				});
			} catch {
				// do nothing
			}

			// if user is already registered to our server set the access token to redux
			if (isRegistered) {
				await refreshAccess(userRef.user.stsTokenManager.refreshToken);
				await FirebaseAuthService.logoutUser();

				if (state?.redirectTo) {
					navigate(state.redirectTo.pathname + state.redirectTo.search);
				} else {
					navigate(PATHS.BASE);
				}
			} else {
				navigate(
					{ pathname: PATHS.REGISTER_WITH_PROVIDER, search: searchParams.toString() },
					{ state: { registered: false, from: 'signInWithGoogle' } }
				);
			}
		} catch (error) {
			showError({
				header: 'unableToLogin',
				message: 'googleAuthFailed'
			});
			AppLogger.error(error);
		}

		dispatch(setLoading(false));
	};

	useEffect(() => {
		if (autoLogin) return;

		try {
			window.google.accounts.id.initialize({
				// eslint-disable-next-line camelcase
				client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
				callback: handleGoogleLogin
			});
			window.google.accounts.id.prompt();
		} catch (error) {
			AppLogger.error('error', error);
		}
	}, [autoLogin]);

	return { handleGoogleLogin };
};

export default useFedCM;
